import Table from "components/Tables/Table";
import React, { useState } from "react";
import ShowDateForList from "show/components/ShowDateForList";
import ShowNameForListInternal from "show/components/ShowNameForListInternal";
import ShowTime from "show/components/ShowTime";
import { getMoneyFormatter } from "utilities/formatter";
import { useCustomQuery, useManualPagination } from "utilities/hookutils";

interface Props {
  queryKey: any[];
  perPage: number;
  instruction: Partial<server.dto.ListShowInternalInstruction>;
  button1?: (show: server.dto.ShowForListInternal) => React.ReactNode;
  button2?: (show: server.dto.ShowForListInternal) => React.ReactNode;
  onFetch: (result: server.dto.ApiResponseWrapperWithRequest<server.dto.ShowForListInternal[], server.dto.ListShowInternalFilters>) => void;
}

export const ShowListWithSales = (props: Props) => {
  const moneyFormatter = getMoneyFormatter();
  const buttonCount = (props.button1 ? 1 : 0) + (props.button2 ? 1 : 0);

  const [instruction, setInstruction] = useState<server.dto.ListShowInternalInstruction>({
    page: 1,
    perPage: props.perPage,
    ...props.instruction,
  });

  const api = useCustomQuery({
    queryFn: () => window.CoreApi.Show.PaginatedInternalEnvironment(instruction).then(res => res.data),
    queryKey: [...props.queryKey, "show-list-with-sales", instruction],
    onSuccess: (response) => {
      pagination.setPagination(response.pagination);
      props.onFetch(response);
      totalsApi.refetch();
    }
  });

  const totalsApi = useCustomQuery({
    queryFn: () => window.CoreApi.Show.SumInternalEnvironment(instruction),
    queryKey: ["showsTotals", instruction, props.queryKey],
  });

  const paginate = (paginationData: Partial<server.dto.PaginationData>) => {
    setInstruction({ ...instruction, page: paginationData.page, perPage: paginationData.perPage });
  };

  const pagination = useManualPagination(instruction.page || 1, props.perPage, paginate);
  const items = api.data?.data || [];
  const totals = totalsApi.data?.data?.data || [];
  const isLoading = api.isLoading || totalsApi.isLoading;

  // TODO: We should get rid of this asap, once we migrate the old document detail pages.
  const isOnMainWebUI = !document.getElementById("app");

  const getFooter = () => {
    const buttonsNullArray = new Array(buttonCount).fill(null);

    if (totals.length === 0) {
      return undefined;
    }

    return totals.filter(total => total.sumSalesExTax > 0).map(total => ({
      key: total.currency,
      className: "s1_cell_financialamount panel-header-bg",
      entries: [null, null, null, `${moneyFormatter.format(total.sumSalesExTax)} ${total.currency.toUpperCase()}`].concat(buttonsNullArray),
      buttonCount
    }));
  }

  const onChange = () => {
    totalsApi.refetch();
    api.refetch();
  }

  return <>
    {pagination.pageCount > 1 && <div className="flex justify-end padding">
      {pagination.Component}
    </div>}
    <Table
      items={items}
      isLoading={isLoading}
      getKey={show => show.showId.toString()}
      hideHeader
      columns={[{
        key: "dateTime",
        header: "",
        render: (show) => <div className="flex-column">
          <ShowDateForList dateTime={show.dateTime} />
          <ShowTime dateTime={show.dateTime} />
        </div>,
        size: "132px"
      }, {
        key: "name",
        header: "",
        render: (show) => <ShowNameForListInternal
          show={show}
          useSidePanel={!isOnMainWebUI}
          onChange={onChange}
          hideCostCenter
          hideProduction
        />,
        size: "1fr"
      }, {
        key: "artistVenuePromoter",
        header: "",
        render: (show) => <ArtistVenuePromoter show={show} />,
        size: "minmax(80px, 1fr)",
      }, {
        key: "sales",
        header: "",
        hidden: !site.permissions.show.canReadSales,
        render: (show) => <span>{show.salesExcludingTax.amount !== 0 && show.salesExcludingTax.formatted}</span>,
        className: "s1_cell_financialamount",
        size: "auto"
      }, {
        key: "button1",
        header: "",
        hidden: !props.button1,
        render: (show) => props.button1?.(show),
        size: "30px"
      }, {
        key: "button2",
        header: "",
        hidden: !props.button2,
        render: (show) => props.button2?.(show),
        size: "30px"
      }]}
      footers={getFooter()}
    />
  </>
}

const ArtistVenuePromoter = ({ show }: { show: server.dto.ShowForListInternal }) => {
  const hasArtist = site.currentEnvironment.functionGroups.artist.enabled && (show.artist);
  const hasVenue = site.currentEnvironment.functionGroups.venue.enabled && (show.venue);
  const hasPromoter = site.currentEnvironment.functionGroups.promoter.enabled && (show.promoterName);

  const venueName = `${show.venue?.name}${show.venue?.cityCountry ? `, ${show.venue.cityCountry}` : ""}`;

  return <div className="show-list-card">
    {hasArtist && <>
      <span className="show-list-card-label">{site.currentEnvironment.functionGroups.artist.singular}</span>
      <span className="text-ellipsis" title={show.artist.name}>{show.artist.name}</span>
    </>}

    {hasVenue && <>
      <div className="show-list-card-label">{site.currentEnvironment.functionGroups.venue.singular}</div>
      <div className="text-ellipsis" title={venueName}>
        {venueName}
      </div>
    </>}

    {hasPromoter && <>
      <span className="show-list-card-label ">{site.currentEnvironment.functionGroups.promoter.singular}</span>
      <span className="text-ellipsis" title={show.promoterName}>{show.promoterName}</span>
    </>}
  </div>;
};